import { CustomStatusMapper } from '@csp/csp-common-custom-status';
import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  BioComplianceStatusValueType,
  BioSample,
  BioSampleCodeFormatType,
  BioSampleCommentValueType,
  BioSampleContainerNumber,
  BioSampleDeliveryLab,
  BioSampleProcessedValueType,
  BioSampleRef,
  BioSampleRefType,
  BioSampleStatusType,
  BioSampleStatusValueType,
  BioSampleValueType,
  CollectionStatus,
  CollectionStatusValueType,
  ProcessingStatus,
  ProcessingStatusValueType,
  SampleStatus,
  ShippingStatus,
  ShippingStatusValueType,
} from '@csp/csp-common-hbs-model';
import { AuditMapper } from '@csp/csp-common-mapper';
import { CustomStatus, CustomStatusValue, CustomStatuses, Maybe, ZonedDateTime } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { BioSampleV1 } from '@csp/dmdp-api-hbs-dto';
import { orderBy } from 'lodash';
import { BioSampleCodeFormatterUtil } from '../util/BioSampleCodeFormatterUtil';
import { BioSampleMetaMapper } from './BioSampleMetaMapper';

const toBioSampleContainerNumber = (
  containerNumber: Maybe<BioSampleContainerNumber>,
  codeFormat: BioSampleCodeFormatType,
): Pick<BioSampleRef, 'containerNumber' | 'displayContainerNumber'> => ({
  containerNumber: containerNumber
    ? BioSampleCodeFormatterUtil.toBioSampleContainerNumber(containerNumber, [codeFormat])
    : undefined,
  displayContainerNumber: containerNumber
    ? BioSampleCodeFormatterUtil.toBioSampleDisplayContainerNumber(containerNumber, [codeFormat])
    : undefined,
});

const toUnknown = (type: BioSampleStatusType): CustomStatus =>
  CustomStatusMapper.fromCustomStatusV1({
    type,
    value: BioSampleStatusValueType.UNKNOWN,
    createdAt: ZonedDateTimeFormatter.toZonedDateTimeString(ZonedDateTimeFactory.now()),
  });

const toUnknownProcessingStatusValue = (): CustomStatusValue<ProcessingStatusValueType> => ({
  invalid: false,
  value: ProcessingStatusValueType.UNKNOWN,
  timestamp: ZonedDateTimeFactory.now(),
  createdTimestamp: ZonedDateTimeFactory.now(),
});

const toStatusOrUnknown = <T>(type: BioSampleStatusType, customStatuses: CustomStatuses): CustomStatus<T> =>
  (customStatuses.getByType<BioSampleValueType>(type) ?? toUnknown(type)) as CustomStatus<T>;

const toProcessingStatusOrUnknown = (customStatuses: CustomStatuses): ProcessingStatus => {
  const customStatus = customStatuses.getByType<ProcessingStatusValueType>(BioSampleStatusType.PROCESSING);
  const statuses = customStatus?.getStatusValuesInSequenceNewestFirst() ?? [];
  const unknownProcessingStatusValue = toUnknownProcessingStatusValue();
  const now = Date.now();

  let evaluatedStatusValue = unknownProcessingStatusValue;
  let startedAt: Maybe<ZonedDateTime>;
  let endedAt: Maybe<ZonedDateTime>;
  if (statuses.length === 1 && statuses[0]?.value === ProcessingStatusValueType.IN_PROCESS) {
    evaluatedStatusValue = statuses[0];
    startedAt = statuses[0].localTimestamp;
  } else {
    const processedStatus = statuses[0];
    const inProcessStatus = statuses[1];
    evaluatedStatusValue =
      processedStatus &&
      inProcessStatus &&
      processedStatus.value === ProcessingStatusValueType.PROCESSED &&
      inProcessStatus.value === ProcessingStatusValueType.IN_PROCESS &&
      processedStatus.localTimestamp
        ? processedStatus.localTimestamp.unixTimeMillis < now
          ? processedStatus
          : inProcessStatus
        : processedStatus ?? unknownProcessingStatusValue;
    startedAt = inProcessStatus?.localTimestamp;
    endedAt = processedStatus?.localTimestamp;
  }

  return {
    evaluatedStatusValue,
    startedAt,
    endedAt,
  };
};

const toSampleStatuses = (
  v1: BioSampleV1,
): {
  collectionStatus: CollectionStatus;
  processingStatus: ProcessingStatus;
  shippingStatus: ShippingStatus;
  sampleStatus: SampleStatus;
  customStatuses: CustomStatuses;
} => {
  const customStatuses = CustomStatusMapper.fromCustomStatusesV1(v1.customStatuses ?? []);
  const collectionStatus = toStatusOrUnknown<CollectionStatusValueType>(BioSampleStatusType.COLLECTION, customStatuses);
  const processingStatus = toProcessingStatusOrUnknown(customStatuses);
  const shippingStatus = toStatusOrUnknown<ShippingStatusValueType>(BioSampleStatusType.SHIPPING, customStatuses);
  const sampleStatus =
    shippingStatus.value !== ShippingStatusValueType.UNKNOWN
      ? shippingStatus
      : processingStatus.evaluatedStatusValue.value !== ProcessingStatusValueType.UNKNOWN
        ? processingStatus.evaluatedStatusValue
        : collectionStatus;

  return {
    collectionStatus,
    processingStatus,
    shippingStatus,
    sampleStatus,
    customStatuses,
  };
};

const toBioSample = (v1: BioSampleV1): BioSample => {
  const { sampleStatus, collectionStatus, processingStatus, shippingStatus, customStatuses } = toSampleStatuses(v1);
  const commentsStatus = toStatusOrUnknown<BioSampleCommentValueType>(BioSampleStatusType.COMMENT, customStatuses);
  const currentCommentStatusValues = commentsStatus.getStatusValuesInSequenceNewestFirst().filter(v => !!v.reasonText);
  const previousCommentStatusValues = commentsStatus.previous.filter(v => !!v.reasonText);
  const commentStatusValues = orderBy(
    [...previousCommentStatusValues, ...currentCommentStatusValues],
    status => status.createdTimestamp.unixTimeMillis,
    'asc',
  );

  const isAliquotParent = BioSampleMetaMapper.getAliquotOrDefault(v1.metas).parent;
  const aliquotParentSampleId =
    v1.refs?.find(refV1 => refV1.key === BioSampleRefType.ALIQUOT_PARENT_ID)?.value ?? undefined;
  const isAliquotChild = Boolean(aliquotParentSampleId);

  const format = EnumUtil.fromMaybeStringOrDefault(
    v1.sampleCodeFormat,
    BioSampleCodeFormatType,
    BioSampleCodeFormatType.UNKNOWN,
  );
  return {
    ...AuditMapper.fromV1(v1.audit),
    ...toBioSampleContainerNumber(v1.sampleContainerNumber, format),
    sampleId: v1.bioSampleId,
    sampleCode: v1.sampleCode,
    kitCode: v1.sampleKitCode ?? '-',
    containerDescription: v1.containerDescription,
    containerType: v1.containerType,
    kitDescription: v1.kitDescription,
    kitExpiryDate: v1.kitExpirationDate,
    kitType: v1.kitType,
    isAliquotParent,
    isAliquotChild,
    aliquotParentSampleId,
    format,
    visitRequestRef: v1.scheduleRequestRef
      ? {
          requestCode: v1.scheduleRequestRef.requestCode,
          scheduleCode: v1.scheduleRequestRef.scheduleCode,
          versionCode: v1.scheduleRequestRef.versionCode,
        }
      : undefined,
    visitId: v1.refs?.find(refV1 => refV1.key === BioSampleRefType.VISIT_ID)?.value ?? undefined,
    patientId: v1.userId,
    sampleStatus,
    collectionStatus,
    processingStatus,
    shippingStatus,
    sampleType: toStatusOrUnknown<BioSampleValueType>(BioSampleStatusType.SAMPLE_TYPE, customStatuses),
    processedSampleType: toStatusOrUnknown<BioSampleProcessedValueType>(
      BioSampleStatusType.PROCESSED_SAMPLE_TYPE,
      customStatuses,
    ),
    analyteType: toStatusOrUnknown<string>(BioSampleStatusType.ANALYTE_TYPE, customStatuses),
    processComplianceStatus: toStatusOrUnknown<BioComplianceStatusValueType>(
      BioSampleStatusType.PROCESS_COMPLIANCE,
      customStatuses,
    ),
    storageComplianceStatus: toStatusOrUnknown<BioComplianceStatusValueType>(
      BioSampleStatusType.STORAGE_COMPLIANCE,
      customStatuses,
    ),
    deliveryLab: toStatusOrUnknown<BioSampleDeliveryLab>(BioSampleStatusType.DELIVERY_LAB, customStatuses),
    shippingNumber: toStatusOrUnknown<ShippingStatusValueType>(BioSampleStatusType.SHIPPING_NUMBER, customStatuses),
    commentStatusValues,

    comments: [
      collectionStatus.reasonText,
      processingStatus.evaluatedStatusValue.reasonText,
      shippingStatus.reasonText,
      ...commentStatusValues.map(value => value.reasonText),
    ].filter(isDefined),
  };
};

const toBioSamples = (v1s: BioSampleV1[]): BioSample[] => v1s.map(toBioSample);

export const BioSampleMapper = {
  toBioSampleContainerNumber,
  toBioSample,
  toBioSamples,
  toUnknownProcessingStatusValue,
};
